import React, { useState, useRef, useEffect } from 'react';

/**
 * Single-level multi-select dropdown with checkboxes.
 *
 * Props:
 *   items       – ['Michael Blythe', 'Linda Mitchell', ...]
 *   value       – [] (array of selected items)
 *   onChange     – (newArray) => void
 *   placeholder  – text when nothing selected
 */
const MultiSelect = ({ items = [], value = [], onChange, placeholder = 'All' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const toggle = (item) => {
    const next = value.includes(item)
      ? value.filter((x) => x !== item)
      : [...value, item];
    onChange(next);
  };

  const clearAll = () => onChange([]);

  const selectAll = () => onChange([...items]);

  const allSelected = value.length === items.length;

  let displayLabel = placeholder;
  if (value.length === 1) {
    displayLabel = value[0];
  } else if (value.length > 1) {
    displayLabel = `${value.length} selected`;
  }

  return (
    <div className="drilldown-select" ref={ref}>
      <button
        className={`drilldown-trigger ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        type="button"
      >
        <span className="drilldown-trigger-text">{displayLabel}</span>
        <svg className="drilldown-chevron" width="10" height="6" viewBox="0 0 10 6" fill="none">
          <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {isOpen && (
        <div className="drilldown-panel">
          <div className="drilldown-bar">
            {value.length > 0 ? (
              <button className="drilldown-clear-btn" onClick={clearAll} type="button">
                Clear all
              </button>
            ) : (
              <button className="drilldown-clear-btn" onClick={selectAll} type="button">
                Select all
              </button>
            )}
            {!allSelected && value.length > 0 && (
              <button className="drilldown-clear-btn" onClick={selectAll} type="button">
                Select all
              </button>
            )}
          </div>

          {items.map((item) => (
            <label key={item} className="drilldown-check-label drilldown-single-item">
              <input
                type="checkbox"
                checked={value.includes(item)}
                onChange={() => toggle(item)}
              />
              <span className="drilldown-checkmark" />
              <span className="drilldown-check-text">{item}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

export default MultiSelect;
